<script setup lang="ts">
import { ref, onMounted, onUpdated } from "vue";

import { hasValue } from "@/Utils/Objects";

export interface TabsProps {
  disabled?: boolean;
  items: {
    title: string;
    value: string;
  }[];
  defValue?: string;
}

const props = defineProps<TabsProps>();

const tabSelected = ref<string>("");

const emit = defineEmits<{
  (event: "tabSelected", value: string): void;
}>();

const onTabClicked = (val: string) => {
  emit("tabSelected", val);

  tabSelected.value = val;
};

const selectTab = () => {
  if (hasValue(props.defValue)) {
    tabSelected.value = props.defValue;
  } else if (props.items.length > 0) {
    tabSelected.value = props.items[0]!.value;
  }
};

onMounted(selectTab);
onUpdated(selectTab);
</script>

<template>
  <div class="tabs">
    <div
      v-for="item in items"
      class="tabs__option"
      :class="{ 'tabs__option--selected': item.value === tabSelected }"
      :key="item.value"
      :style="{ width: `${100 / items.length}%` }"
      @click="() => onTabClicked(item.value)"
    >
      <span>
        {{ item.title }}
      </span>

      <div class="tabs__option__border" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tabs {
  @include border(2, solid, b);
  @include colors.border(content, low);

  display: flex;
  width: 100%;
  flex-wrap: nowrap;

  &__option {
    @include sp.padding(false, 3);

    cursor: pointer;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;

    &--selected {
      & > span {
        @include colors.color(content, high, true);
      }
      .tabs__option__border {
        @include colors.bg(content, high);
      }
    }

    & > span {
      @include text.general(high, s);
      @include colors.color(content, mid);

      text-transform: capitalize;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }

    &__border {
      position: absolute;
      height: 2px;
      width: 100%;
      background-color: transparent;
      bottom: -2px;
    }
  }
}
</style>
